import {useQuery} from '@apollo/client';
import {useBuilderContext} from '../CreateBuilder/useBuilderContext';
import {useAutoForm} from '../../../components/AutoForm/useAutoForm';
import {sanitiseProductType} from '../../../utils/sanitiseProductType';
import validateUUID from '../../../utils/validateUUID';
import {BUILDER_WITHDRAW_DESTINATIONS, BUILDER_WITHDRAW_POTS} from '../builderPotQueries';
import {reccNonEditable} from '../CreateBuilder/BuilderSourceDestination/CreateBuilderFooter';
import {builderFieldsType} from '../CreateBuilder/builderFields';
import {BuilderPot} from '../../../schema-types/BuilderPot';
import {OvbuilderDetails_ovrecommendDetails} from '../../../schema-types/OvbuilderDetails';
import {
  BuilderWithdrawPots,
  BuilderWithdrawPotsVariables,
} from '../../../schema-types/BuilderWithdrawPots';
import {BuilderBankAccount} from '../../../schema-types/BuilderBankAccount';
import {
  BuilderWithdrawDestinations,
  BuilderWithdrawDestinationsVariables,
} from '../../../schema-types/BuilderWithdrawDestinations';
import {OVRecommendActionEnum} from '../../../schema-types/globalTypes';

// Withdrawal destinations are BankAccount entities (DBO-1436).
export type WithdrawalDestinationAccount = BuilderBankAccount;

const savedDestinationAccount = (
  builderDetails: OvbuilderDetails_ovrecommendDetails | null
): WithdrawalDestinationAccount | null => {
  // New-style withdrawals store a BankAccount destination directly.
  const destination = builderDetails?.withdraw?.destination;
  if (destination) {
    return destination;
  }
  // Legacy saved recommendation — the destination row is a bank pot and
  // withdraw.destination is null; map the pot onto the bank-account shape
  // for read-only display.
  const legacyPot = builderDetails?.potPlans?.find(
    (row) => row.planType === 'destination'
  )?.pot;
  if (!legacyPot) {
    return null;
  }
  return {
    __typename: 'BankAccountType',
    id: legacyPot.id,
    curoId: legacyPot.curoId,
    name: legacyPot.name,
    accountName: legacyPot.policyHolder,
    accountNumber: legacyPot.planNumber,
    sortCode: legacyPot.sortCode,
    // Pots don't carry a bank-account status; legacy rows show no status.
    status: null,
    isAccountValidated: legacyPot.isValidated ?? false,
    provider: legacyPot.productProvider,
    contact: legacyPot.contact,
  };
};

export const useWithdrawalPotsQuery = (): {
  sourcePots: BuilderPot[];
  destinationAccounts: WithdrawalDestinationAccount[];
  isSourceChosen: boolean;
  areDestinationsLoaded: boolean;
} => {
  const {builderDetails, builderReviewId} = useBuilderContext();
  const {values} = useAutoForm<builderFieldsType>();
  const contactId = values.contact ? validateUUID(values.contact) || null : null;
  const isNonEditable = !!reccNonEditable(builderDetails);
  const reviewId = validateUUID(builderReviewId);
  const sourcePotId = values.withdraw?.source?.id;
  const isWithdraw = values.planAction === OVRecommendActionEnum.WITHDRAW;

  const {data: potsData} = useQuery<BuilderWithdrawPots, BuilderWithdrawPotsVariables>(
    BUILDER_WITHDRAW_POTS,
    {
      variables: {
        reviewId: reviewId!,
        contactId,
        potType: sanitiseProductType(values.mainPlanType) ?? '',
      },
      skip: isNonEditable || !isWithdraw || !values.mainPlanType || !reviewId,
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    }
  );

  const {data: destinationsData, loading: areDestinationsLoading} = useQuery<
    BuilderWithdrawDestinations,
    BuilderWithdrawDestinationsVariables
  >(BUILDER_WITHDRAW_DESTINATIONS, {
    variables: {reviewId: reviewId!, sourcePotId: sourcePotId!, contactId},
    skip: isNonEditable || !isWithdraw || !reviewId || !sourcePotId,
    fetchPolicy: 'cache-and-network',
  });

  if (isNonEditable) {
    const savedSource = builderDetails?.withdraw?.sourcePot;
    const savedDestination = savedDestinationAccount(builderDetails);
    return {
      sourcePots: savedSource ? [savedSource] : [],
      destinationAccounts: savedDestination ? [savedDestination] : [],
      isSourceChosen: !!savedSource,
      areDestinationsLoaded: true,
    };
  }

  return {
    sourcePots: potsData?.withdrawPots?.sources ?? [],
    destinationAccounts: destinationsData?.withdrawDestinationBankAccounts ?? [],
    isSourceChosen: !!sourcePotId,
    areDestinationsLoaded: !!destinationsData && !areDestinationsLoading,
  };
};
